import React from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import ModulesCard from './ModulesCard';
import TimeKeepingOptionsCard, { effectiveShowHours } from './TimeKeepingOptionsCard';

import Button from 'CommonComponents/Button';

import { useConfirmationModalContext } from 'Context/ConfirmationModalContext';

import { useToast } from 'Hooks/useToast';

const OrganisationModulesScheme = Yup.object().shape({
    time_keeping_enabled: Yup.bool().required('This value is required'),
    default_unit: Yup.string().oneOf(['days', 'hours']).required('This value is required'),
    increment: Yup.string().oneOf(['quarter_day', 'half_day', 'whole_day']).required('This value is required'),
    show_hours: Yup.bool().required('This value is required'),
    week_starts: Yup.string().oneOf(['monday', 'sunday']).required('This value is required'),
    hours_in_day: Yup.number().min(1, 'A working day is at least one hour').max(24, 'A working day is at most 24 hours').required('This value is required'),
});

export default function SaveOrganisationModulesForm(props) {
    const confirm = useConfirmationModalContext();
    const toast = useToast();

    const initialValues = {
        time_keeping_enabled: props.timeKeepingEnabled || false,
        default_unit: props.timeKeepingSettings?.default_unit || 'days',
        increment: props.timeKeepingSettings?.increment || 'quarter_day',
        show_hours: props.timeKeepingSettings?.show_hours ?? true,
        week_starts: props.timeKeepingSettings?.week_starts || 'monday',
        hours_in_day: Number(props.timeKeepingSettings?.hours_in_day ?? 8),
    };

    // Only a switch-off needs confirming — turning the module on reveals screens
    // rather than hiding them.
    const confirmDisable = () =>
        confirm({
            title: $t('Disable time keeping'),
            message: $t(
                'Time keeping will be switched off for this organisation. Existing time entries and rate cards are preserved and reappear if you turn it back on.',
            ),
            warning: $t('Every time keeping screen is hidden while the module is off.'),
        });

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={OrganisationModulesScheme}
            onSubmit={async (values, actions) => {
                if (props.timeKeepingEnabled && !values.time_keeping_enabled) {
                    const action = await confirmDisable();

                    if (!action?.confirmed) {
                        // Nothing was written. Put the switch back where the server
                        // still has it, and leave every other edit alone — they
                        // cancelled the disable, not their option changes.
                        actions.setFieldValue('time_keeping_enabled', true);
                        actions.setSubmitting(false);

                        return;
                    }
                }

                // Returned so Formik keeps the form submitting until the request
                // settles; without it the button re-enables mid-flight and a second
                // click fires a second POST.
                return axios
                    .post(props.saveModulesUrl, { ...values, show_hours: effectiveShowHours(values) })
                    .then(() => {
                        props.postSaveHandler();
                    })
                    .catch((e) => {
                        if (e.response?.data?.errors) {
                            actions.setErrors(e.response.data.errors);
                        } else {
                            toast.error($t('Something went wrong'));
                        }
                    })
                    .finally(() => {
                        actions.setSubmitting(false);
                    });
            }}
        >
            {({ values, isSubmitting, handleSubmit }) => (
                <div className="tw-space-y-4">
                    <ModulesCard disabled={isSubmitting} />

                    <TimeKeepingOptionsCard disabled={isSubmitting || !values.time_keeping_enabled} />

                    <div className="tw-flex tw-justify-end">
                        <Button variant="primary" disabled={!!isSubmitting} onClick={handleSubmit}>
                            {$t('Save time keeping')}
                        </Button>
                    </div>
                </div>
            )}
        </Formik>
    );
}
